import { ref } from "vue";
import type {  AxiosRequestConfig } from "axios";
import { useAuthStore } from "@/store/modules/auth";
import { localStg } from "@/utils/storage";
import { useAppStore } from "@/store/modules/app";
import { fetchPing, fetchRefreshToken } from "../api";
import { fetchExchangeRate } from "../api/data";
import { updateUserInfoFromResponse } from "@/utils/export";
import { logoutUser } from "@/service/api";
import { useUserStore } from "@/store/modules/user";
/**
 * refresh token
 *
 * @param axiosConfig - request config when the token is expired
 */
const pingInProgress = ref(false);

function getStatusFromRecord(value: unknown): number | undefined {
  if (!value || typeof value !== "object") return undefined;

  const record = value as Record<string, unknown>;
  if (typeof record.status === "number") return record.status;

  const response = record.response;
  if (response && typeof response === "object") {
    const responseRecord = response as Record<string, unknown>;
    if (typeof responseRecord.status === "number") return responseRecord.status;
  }

  return undefined;
}

function getErrorStatusCode(error: unknown): number | undefined {
  const directStatus = getStatusFromRecord(error);
  if (directStatus !== undefined) return directStatus;
  if (!error || typeof error !== "object") return undefined;

  const toJSON = (error as { toJSON?: unknown }).toJSON;
  if (typeof toJSON !== "function") return undefined;

  try {
    return getStatusFromRecord(toJSON.call(error));
  } catch {
    return undefined;
  }
}

export async function handleRefreshToken(axiosConfig: AxiosRequestConfig) {
  const { resetStore } = useAuthStore();

  const refreshToken = localStg.get("refreshToken") || "";
  const { error, data } = await fetchRefreshToken(refreshToken);
  if (!error) {
    localStg.set("token", data.token);
    localStg.set("refreshToken", data.refreshToken);

    const config = { ...axiosConfig };
    if (config.headers) {
      config.headers.Authorization = data.token;
    }

    return config;
  }

  resetStore();

  return null;
}

export async function handlePingPong(force: boolean = false): Promise<boolean> {
  // Prevent multiple concurrent pings
  if (pingInProgress.value && !force) return false;

  pingInProgress.value = true;

  try {
    const { error, data, statusCode } = await fetchPing();
    if (!error) {
     // Update user info with the response
     if (data) {
      updateUserInfoFromResponse(data);
     }
     return true;
    } else if (statusCode === 403 || getErrorStatusCode(error) === 403) {
      logoutUser();
      const userStore = useUserStore();
      userStore.resetUserInfo();
      const authStore = useAuthStore();
      authStore.resetStore();
      return false;
    } else return false;
  } catch {
    return false;
  } finally {
    pingInProgress.value = false;
  }
}

export const updateExchangeRate = async () => {
  const appStore = useAppStore();

  try {
    const { error, data } = await fetchExchangeRate();
    if (!error && data && data.exchange_rate) {
      appStore.exchangeRate = data.exchange_rate;
    }
  } catch {
    // Keep the cached/default exchange rate when background refresh fails.
  }
};
